import { getLivestreamRankingDateRangeFromSearchParams } from "../../utils/livestreamRankingDateRange";

/**
 * Placeholders for social post templates:
 * - {periodLabel}: e.g. "semana del 16 al 22 de Febrero"
 * - {ranking}: replaced with lines "1. @instagram_handle\n2. @instagram_handle\n..."
 */
export const SOCIAL_POST_PLACEHOLDERS = {
  periodLabel: "{periodLabel}",
  ranking: "{ranking}",
} as const;

/** Placeholders for ranking card footer explainers. */
export const FOOTER_EXPLAINER_PLACEHOLDERS = {
  programCount: "{N}",
} as const;

/** Format for each ranking line when building {ranking}. Use: position + ". " + programName + " (@" + podcastInstagramHandle + ")" */
export const SOCIAL_POST_RANKING_LINE_FORMAT =
  "{position}. {programName} (@{instagramHandle})";

/**
 * Copy for each ranking type. Use these with RadarPodscopeRankingCard so subheader
 * and footer explainer stay consistent and are easy to change per ranking.
 */
export const RANKING_COPY = {
  views: {
    subheader: "Programas x Views\nPromedio",
    footerExplainer:
      "Views promedio por programa en episodios de 15+ minutos, a 7 días de estreno en YouTube, entre {N} programas activos.",
    socialPost: {
      headline: `El ranking de audiencia en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Promedio de views de todos los videos por programa durante el periodo, de más de 5 minutos medidos a los 7 días de su estreno. Solo de programas de conversación en YouTube.",
      howWeMeasure: "",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  channelsViews: {
    subheader: "Canales x Views\nTotal",
    footerExplainer:
      "Suma de views de todos los videos por canal, de 15+ minutos a 7 días de estreno en YouTube, entre {N} canales 100% digitales.",
    socialPost: {
      headline: `El ranking de audiencia en YouTube por canal: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Suma de views a 7 días de todos los videos por canal durante el periodo, de más de 5 minutos. Solo canales de conversación en YouTube.",
      howWeMeasure: "",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  interactions: {
    subheader: "Programas x Interacción\nPromedio",
    footerExplainer:
      "Likes y comentarios promedio de videos de 15+ minutos medidos a 7 días de estreno en YouTube, entre {N} programas activos del ecosistema.",
    socialPost: {
      headline: `El ranking de interacción en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Promediamos likes y comentarios obtenidos por cada episodio de más de 5 minutos de programas de conversación a los 7 días de su estreno en YouTube durante la semana.",
      howWeMeasure: "",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  interactionsTotal: {
    subheader: "Programas x Interacción\nTotal",
    footerExplainer:
      "Suma de likes y comentarios de videos de 15+ minutos medidos a 7 días de estreno en YouTube, entre {N} programas activos del ecosistema.",
    socialPost: {
      headline: `El ranking de interacción total en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Sumamos likes y comentarios obtenidos por todos los episodios de más de 5 minutos de programas de conversación a los 7 días de su estreno en YouTube durante la semana.",
      howWeMeasure: "",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  channelsInteractions: {
    subheader: "Canales x Interacción\nTotal",
    footerExplainer:
      "Suma de likes y comentarios de todos los videos por canal, de 15+ minutos a 7 días de estreno en YouTube, entre {N} canales 100% digitales.",
    socialPost: {
      headline: `El ranking de interacción en YouTube por canal: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Suma de likes y comentarios a 7 días de todos los videos por canal durante el periodo, de más de 5 minutos. Solo canales de conversación en YouTube.",
      howWeMeasure: "",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  channelsSocial: {
    subheader: "Canales x Views en RRSS",
    /** Base label only; ranking assets append publish date via `formatChannelsSocialFooterExplainer` / `buildChannelsSocialFooterExplainer`. */
    footerExplainer:
      "Views de programas activos en YouTube en contenidos publicados en el período en YT Shorts, IG Reels y TikTok",
    socialPost: {
      headline: `El pulso de la distribución en redes: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Los canales expanden su alcance en contenido corto. Así quedó el ranking por views en YouTube Shorts, Instagram Reels y TikTok:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nSumamos las views en YouTube Shorts, Instagram Reels y TikTok de cada canal en el período seleccionado.",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  programsBrands: {
    subheader: "Programas x Marcas",
    footerExplainer:
      "Marcas con mención en contenido, placement, banner o regalos en episodios de 15+ minutos, de un universo de {N} marcas. En empate, se prioriza menciones en contenido, luego views y marcas repetidas de la semana anterior.",
    socialPost: {
      headline: `Programas con más marcas: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Estos programas concentraron la mayor cantidad de marcas distintas con mención en contenido en episodios de 15+ minutos:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nContamos marcas distintas con mención en contenido en episodios de 5+ min. En empate, se prioriza por marcas con menciones en contenido, luego views y luego marcas repetidas de la semana anterior.",
      noteN:
        'ℹ️ Nota: El indicador "N" aparece cuando un programa ingresa al ranking porque la semana anterior no cumplió criterio de publicación.',
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  livestreamPrograms: {
    subheader: "Canales x Espectadores En Vivo Promedio",
    footerExplainer:
      "Promedio de espectadores, peaks y cantidad de emisiones en YouTube durante el periodo, en vivo o estrenos de 15+ minutos",
    socialPost: {
      headline: `Programas live en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Ranking de canales por promedio de espectadores simultáneos durante sus livestreams en el período:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nPara cada transmisión tomamos el promedio de espectadores simultáneos; el indicador del canal es el promedio ponderado por cantidad de streams en el período.",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  /** Same ranking param as `livestreamPrograms`; grouped by Explorer `livestream_program` (not by channel). */
  livestreamProgramsProgramGroup: {
    subheader: "Programas x Espectadores\nEn Vivo",
    footerExplainer:
      "Promedio de espectadores simultáneos y peaks en YouTube, en livestreams de 15+ minutos, entre {N} programas de canales 100% digitales.",
    socialPost: {
      headline: `Programas live en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Ranking de programas live por promedio de espectadores simultáneos durante sus transmisiones en el período:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nAgrupamos streams por programa live en Explorer; para cada grupo el indicador es el promedio de los promedios de espectadores por stream, ponderado por cantidad de streams.",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  worldCupViews: {
    subheader: "Programas x Copa Mundial\nViews Promedio",
    footerExplainer:
      "Views promedio a 7 días de episodios de 15+ minutos cuyo título o descripción menciona Copa Mundial / Mundial 2026 en YouTube",
    socialPost: {
      headline: `Ranking Copa Mundial en YouTube: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Programas de conversación rankeados por views promedio de episodios relacionados con la Copa Mundial FIFA 2026 detectados por palabras clave en título o descripción:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nIncluimos episodios long-form cuyo título o descripción contiene señales de Copa Mundial 2026 (p. ej. copa mundial, mundial 2026, FIFA World Cup). Excluimos falsos positivos como guerra mundial o mundiales de otros deportes.",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
  worldCupLivestreamPrograms: {
    subheader: "Programas Live x Copa Mundial",
    footerExplainer:
      "Promedio de espectadores simultáneos en transmisiones en vivo cuyo título o descripción menciona Copa Mundial / Mundial 2026",
    socialPost: {
      headline: `Ranking Copa Mundial en vivo: ${SOCIAL_POST_PLACEHOLDERS.periodLabel}`,
      body: "Programas live rankeados por espectadores promedio en transmisiones relacionadas con la Copa Mundial FIFA 2026:",
      howWeMeasure:
        "📊 ¿Cómo medimos?\nFiltramos streams por palabras clave de Copa Mundial 2026 en título o descripción; agrupamos por programa live y promediamos espectadores por stream.",
      source: "Fuente: Podscope, inteligencia en conversación digital.",
      moreInsights: "💡 Más insights del ecosistema en Radar Podscope.",
    },
  },
} as const;

/** Spanish month names for channels-social footer publish date (day + month label). */
const RANKING_MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
] as const;

/**
 * Period end → publish label: end date + 8 días at local midnight, as "D de Mes".
 */
function formatPublishDayMonth(periodEndDate: Date): string {
  const publish = new Date(periodEndDate);
  publish.setDate(publish.getDate() + 8);
  publish.setHours(0, 0, 0, 0);
  const day = publish.getDate();
  const month = RANKING_MONTHS_ES[publish.getMonth()];
  return `${day} de ${month}`;
}

/**
 * Channels-social footer with publish date. Uses `RANKING_COPY.channelsSocial.footerExplainer` as the base string.
 * @param formattedDateLabel — e.g. `"5 de Marzo"` from `formatPublishDayMonth` or your own formatter.
 */
export function formatChannelsSocialFooterExplainer(
  formattedDateLabel: string,
): string {
  return `${RANKING_COPY.channelsSocial.footerExplainer} medidos el ${formattedDateLabel}`;
}

/**
 * Resolves period from URL params, then full footer (base copy + date). Falls back to base only if period unknown.
 */
export function buildChannelsSocialFooterExplainer(
  week: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
): string {
  const base = RANKING_COPY.channelsSocial.footerExplainer;
  const range = getLivestreamRankingDateRangeFromSearchParams(
    week,
    startDate,
    endDate,
  );
  if (!range?.endDate) return base;
  const end = new Date(`${range.endDate}T12:00:00`);
  if (Number.isNaN(end.getTime())) return base;
  end.setDate(end.getDate() - 1);
  return formatChannelsSocialFooterExplainer(formatPublishDayMonth(end));
}

/** Views ranking footer (replaces `{N}` with total programs measured). */
export function formatViewsFooterExplainer(programCount: number): string {
  return RANKING_COPY.views.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(programCount),
  );
}

/** Interactions ranking footer (replaces `{N}` with total programs measured). */
export function formatInteractionsFooterExplainer(
  programCount: number,
): string {
  return RANKING_COPY.interactions.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(programCount),
  );
}

/** Total interactions ranking footer (replaces `{N}` with total programs measured). */
export function formatInteractionsTotalFooterExplainer(
  programCount: number,
): string {
  return RANKING_COPY.interactionsTotal.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(programCount),
  );
}

/** Channels total-views ranking footer (replaces `{N}` with digital channel count). */
export function formatChannelsViewsFooterExplainer(channelCount: number): string {
  return RANKING_COPY.channelsViews.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(channelCount),
  );
}

/** Channels total-interactions ranking footer (replaces `{N}` with digital channel count). */
export function formatChannelsInteractionsFooterExplainer(
  channelCount: number,
): string {
  return RANKING_COPY.channelsInteractions.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(channelCount),
  );
}

/** Programs-brands ranking footer (replaces `{N}` with distinct paid brands in the period). */
export function formatProgramsBrandsFooterExplainer(
  totalUniqueBrands: number,
): string {
  return RANKING_COPY.programsBrands.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(totalUniqueBrands),
  );
}

/** Same brands ranking, limited to 100% digital programs on TV channels. */
export const TV_DIGITAL_PROGRAMS_BRANDS_SUBHEADER =
  "Programas digitales\nde TV x Marcas";

export function formatTvDigitalProgramsBrandsFooterExplainer(
  totalUniqueBrands: number,
): string {
  return `Marcas con mención en contenido, placement, banner o regalos en episodios de 15+ minutos de programas 100% digitales en canales de TV, de un universo de ${totalUniqueBrands} marcas. En empate, se prioriza menciones en contenido, luego views y marcas repetidas de la semana anterior.`;
}

/** Brands ranking across digital channels and 100% digital programs on TV. */
export const MIXED_DIGITAL_TV_BRANDS_SUBHEADER =
  "Programas digitales\ny de TV x Marcas";

export function formatMixedDigitalTvBrandsFooterExplainer(
  totalUniqueBrands: number,
): string {
  return `Marcas con mención en contenido, placement, banner o regalos en episodios de 15+ minutos de canales 100% digitales y programas digitales de TV, de un universo de ${totalUniqueBrands} marcas. En empate, se prioriza menciones en contenido, luego views y marcas repetidas de la semana anterior.`;
}

/** Digital livestream programs ranking footer (replaces `{N}` with tracked program count). */
export function formatLivestreamProgramsProgramGroupFooterExplainer(
  programCount: number,
): string {
  return RANKING_COPY.livestreamProgramsProgramGroup.footerExplainer.replace(
    FOOTER_EXPLAINER_PLACEHOLDERS.programCount,
    String(programCount),
  );
}

export type RankingCopyKey = keyof typeof RANKING_COPY;

/** URL param value for each ranking. Must match backend and frontend routing. */
export const RANKING_PARAM = {
  views: "views",
  channelsViews: "channels-views",
  interactions: "interactions",
  interactionsTotal: "interactions-total",
  channelsInteractions: "channels-interactions",
  channelsSocial: "channels-social",
  programsBrands: "programs-brands",
  livestreamPrograms: "livestream-programs",
  /** Comunidad grouped by Explorer `livestream_program` (not by channel). */
  livestreamProgramsProgram: "livestream-programs-program",
  worldCupViews: "world-cup-views",
  worldCupLivestreamPrograms: "world-cup-livestream-programs",
} as const;

/** Livestream ranking assets (channel or program group). */
export function isExplorerLivestreamRankingParam(param: RankingParam): boolean {
  return (
    param === RANKING_PARAM.livestreamPrograms ||
    param === RANKING_PARAM.livestreamProgramsProgram
  );
}

/** Copa Mundial ranking assets (YouTube programs or livestream program grouping). */
export function isWorldCupRankingParam(param: RankingParam): boolean {
  return (
    param === RANKING_PARAM.worldCupViews ||
    param === RANKING_PARAM.worldCupLivestreamPrograms
  );
}

export type RankingParam = (typeof RANKING_PARAM)[keyof typeof RANKING_PARAM];

/** Single source of truth: param value -> copy key and label for Programs Analysis tab. */
export const RANKING_TYPES: {
  param: RankingParam;
  copyKey: RankingCopyKey;
  label: string;
}[] = [
  {
    param: "channels-social",
    copyKey: "channelsSocial",
    label: "Canales por views en redes (contenido corto)",
  },
  { param: "views", copyKey: "views", label: "Programas por views promedio" },
  {
    param: "channels-views",
    copyKey: "channelsViews",
    label: "Canales por total de views",
  },
  {
    param: "interactions",
    copyKey: "interactions",
    label: "Programas por interacciones promedio",
  },
  {
    param: "interactions-total",
    copyKey: "interactionsTotal",
    label: "Programas por interacciones totales",
  },
  {
    param: "channels-interactions",
    copyKey: "channelsInteractions",
    label: "Canales por interacciones totales",
  },
  {
    param: "livestream-programs",
    copyKey: "livestreamPrograms",
    label: "Programas live por espectadores promedio (por canal)",
  },
  {
    param: "livestream-programs-program",
    copyKey: "livestreamProgramsProgramGroup",
    label: "Programas live por espectadores promedio (por programa live)",
  },
  {
    param: "programs-brands",
    copyKey: "programsBrands",
    label: "Programas por cantidad de marcas",
  },
  {
    param: "world-cup-views",
    copyKey: "worldCupViews",
    label: "Programas YouTube por views (Copa Mundial)",
  },
  {
    param: "world-cup-livestream-programs",
    copyKey: "worldCupLivestreamPrograms",
    label: "Programas live por espectadores (Copa Mundial)",
  },
];

export const RANKING_PARAM_VALUES = Object.values(
  RANKING_PARAM,
) as RankingParam[];

/** Item for building social post ranking lines: position (1-based), program name, and podcast Instagram handle (without @). */
export type SocialPostRankingItem = {
  position: number;
  programName: string;
  instagramHandle: string;
};

/**
 * Build the ranking block for LinkedIn/Instagram: one line per item as "1. Program Name (@handle)", "2. ...", ...
 * @param options.includeHandles - If false, omits the (@handle) part (for LinkedIn copy).
 */
export function buildSocialPostRankingLines(
  items: SocialPostRankingItem[],
  options?: { includeHandles?: boolean },
): string {
  const includeHandles = options?.includeHandles !== false;
  return items
    .map(({ position, programName, instagramHandle }) =>
      includeHandles && instagramHandle
        ? `${position}. ${programName} (@${instagramHandle})`
        : `${position}. ${programName}`,
    )
    .join("\n");
}

const MORE_INSIGHTS_LINK_SUFFIX = ": radar.podscope.co";

/**
 * Build full LinkedIn/Instagram post text for a ranking type.
 * Replaces {periodLabel} with the given label and {ranking} with the built ranking lines.
 * @param options.includeRanking - If false, omits the body and ranking lines (for IG copy).
 * @param options.omitMoreInsightsLink - If true, removes "radar.podscope.co" from the moreInsights line (for LinkedIn copy).
 */
export function buildSocialPostCopy(
  copyKey: RankingCopyKey,
  periodLabel: string,
  rankingLines: string,
  options?: { includeRanking?: boolean; omitMoreInsightsLink?: boolean },
): string {
  const includeRanking = options?.includeRanking !== false;
  const copy = RANKING_COPY[copyKey];
  const social = (copy as { socialPost?: typeof RANKING_COPY.views.socialPost })
    .socialPost;
  if (!social) return "";

  const headline = social.headline.replace(
    SOCIAL_POST_PLACEHOLDERS.periodLabel,
    periodLabel,
  );
  let moreInsights =
    "moreInsights" in social && social.moreInsights ? social.moreInsights : "";
  if (
    options?.omitMoreInsightsLink &&
    moreInsights.endsWith(MORE_INSIGHTS_LINK_SUFFIX)
  ) {
    moreInsights = moreInsights.slice(0, -MORE_INSIGHTS_LINK_SUFFIX.length);
  }
  const parts = [
    headline,
    "",
    ...(social.body ? [social.body, ""] : []),
    ...(includeRanking && rankingLines ? [rankingLines, ""] : []),
    ...(social.howWeMeasure ? [social.howWeMeasure] : []),
    ...("noteN" in social && social.noteN ? ["", social.noteN] : []),
    ...(moreInsights ? ["", moreInsights] : []),
    "",
    social.source,
  ];
  return parts.join("\n");
}
